import { RouteBox, RoutePoint, RouteSegment, RouteSide } from './edge-routing.types'

export const toRouteBox = (x: number, y: number, width: number, height: number): RouteBox => ({
  left: x,
  top: y,
  right: x + width,
  bottom: y + height,
})

export const padBox = (box: RouteBox, amount: number): RouteBox => ({
  left: box.left - amount,
  top: box.top - amount,
  right: box.right + amount,
  bottom: box.bottom + amount,
})

export const manhattan = (a: RoutePoint, b: RoutePoint): number =>
  Math.abs(a.x - b.x) + Math.abs(a.y - b.y)

export const samePoint = (a: RoutePoint, b: RoutePoint): boolean => a.x === b.x && a.y === b.y

export function moveOutward(point: RoutePoint, side: RouteSide, length: number): RoutePoint {
  switch (side) {
    case 'top':
      return { x: point.x, y: point.y - length }
    case 'right':
      return { x: point.x + length, y: point.y }
    case 'bottom':
      return { x: point.x, y: point.y + length }
    case 'left':
      return { x: point.x - length, y: point.y }
  }
}

export const toSegments = (points: RoutePoint[]): RouteSegment[] =>
  points.slice(1).map((end, index) => ({ start: points[index], end }))

export const segmentLength = ({ start, end }: RouteSegment): number => manhattan(start, end)

/**
 * Whether an axis-aligned segment runs through the INTERIOR of a box. Touching the border is
 * allowed on purpose: a handle stub starts on its own node's border and is not a collision.
 * Diagonal segments never belong in an orthogonal route, so they always count as a hit.
 */
export function segmentHitsBox(a: RoutePoint, b: RoutePoint, box: RouteBox): boolean {
  if (a.x === b.x) {
    return (
      a.x > box.left &&
      a.x < box.right &&
      Math.max(a.y, b.y) > box.top &&
      Math.min(a.y, b.y) < box.bottom
    )
  }
  if (a.y === b.y) {
    return (
      a.y > box.top &&
      a.y < box.bottom &&
      Math.max(a.x, b.x) > box.left &&
      Math.min(a.x, b.x) < box.right
    )
  }

  return true
}

export const routeHitsBox = (points: RoutePoint[], box: RouteBox): boolean =>
  toSegments(points).some(({ start, end }) => segmentHitsBox(start, end, box))

/**
 * Drops repeated points and collinear middle points, but KEEPS reversals: a route that leaves a
 * handle and immediately doubles back must keep its short outward stub, or it seems to start from
 * inside its own node.
 */
export function simplifyRoute(points: RoutePoint[]): RoutePoint[] {
  return points.reduce<RoutePoint[]>((result, point) => {
    if (result.length && samePoint(result[result.length - 1], point)) {
      return result
    }
    while (result.length >= 2) {
      const a = result[result.length - 2]
      const b = result[result.length - 1]
      const sameVerticalRun = a.x === b.x && b.x === point.x && (b.y - a.y) * (point.y - b.y) >= 0
      const sameHorizontalRun = a.y === b.y && b.y === point.y && (b.x - a.x) * (point.x - b.x) >= 0
      if (!sameVerticalRun && !sameHorizontalRun) {
        break
      }
      result.pop()
    }
    result.push(point)

    return result
  }, [])
}

/** The point at fraction `t` (0..1) of a polyline's length; 0.5 is where labels sit. */
export function polylinePointAt(points: RoutePoint[], t = 0.5): RoutePoint {
  const segments = toSegments(points)
  const total = segments.reduce((sum, segment) => sum + segmentLength(segment), 0)
  let remaining = total * Math.min(1, Math.max(0, t))
  for (const segment of segments) {
    const length = segmentLength(segment)
    if (remaining <= length) {
      const ratio = length === 0 ? 0 : remaining / length

      return {
        x: segment.start.x + (segment.end.x - segment.start.x) * ratio,
        y: segment.start.y + (segment.end.y - segment.start.y) * ratio,
      }
    }
    remaining -= length
  }

  return points[points.length - 1] ?? { x: 0, y: 0 }
}
